import { ref, onMounted } from 'vue'
import { useGps } from './useGps'
import { useLocationCompare } from './useLocationCompare'
import { GoogleAnalytics } from '~/google_analytics/events'
import { EventParameter } from '~/google_analytics/events.enum'
import { add } from '@amplitude/analytics-browser'

export function useAutoFetchLocation() {
  const runtimeConfig = useRuntimeConfig()

  const isFetchingLocation = ref(false)
  const loadingMessage = ref('')

  const LOCATION_PERMISSION_STATUS = "location_permission_status"
  const excludePartners = ['oneindiamall']

  const location = useLocation()
  const geolocation = useGeoLocation()
  const { addressText } = useAddressText(location)
  const { getLocationOnceSafe } = useGps()
  const { checkIfLocationsAreSame } = useLocationCompare()
  const { $callPermissionFromBFLApp } = useNuxtApp()

  // -------------------- Permission Handler --------------------
  const ensureLocationPermission = async () => {
    const cachedStatus = sessionStorage.getItem(LOCATION_PERMISSION_STATUS)

    // Previously denied
    if (cachedStatus === "denied") {
      GoogleAnalytics.locationEvent({
        [EventParameter.ACTION]: 'location_permission_denied',
        reason: 'user_denied_previous',
        location_available:"no",
        prompt_type:"unknown", 
        needs_location_fetch:"yes",
        lat : location.gps?.latitude || '0',
        long : location.gps?.longitude || '0',
        address: addressText.value || '0',
      })
      return false
    }

    // Previously granted
    if (cachedStatus === "granted") {
      GoogleAnalytics.locationEvent({
        [EventParameter.ACTION]: 'location_permission_granted',
        reason: 'user_granted_previous',
        location_available: 'yes',
        prompt_type:"unknown", 
        needs_location_fetch: 'no',
        lat : location.gps?.latitude || null,
        long : location.gps?.longitude || null,
        address: addressText.value || null,

      })
      return true
    }

    // Ask native permission
    try {

      const result = await $callPermissionFromBFLApp('location')

      if (result.status === "success") {
        sessionStorage.setItem(LOCATION_PERMISSION_STATUS, "granted")
        GoogleAnalytics.locationEvent({
          [EventParameter.ACTION]: 'location_permission_granted',
          reason: 'user_granted_now',
          location_available:"yes",
          prompt_type:"unknown", 
          needs_location_fetch: "yes",
          lat : location.gps?.latitude || null,
          long : location.gps?.longitude || null,
          address: addressText.value || null,
        })
        return true
      } else {
        sessionStorage.setItem(LOCATION_PERMISSION_STATUS, "denied")
        GoogleAnalytics.locationEvent({
          [EventParameter.ACTION]: 'location_permission_denied',
          reason: 'user_denied_now',
          location_available:"no",
          prompt_type:"unknown", 
          needs_location_fetch:"yes",
          lat : location.gps?.latitude || '0',
          long : location.gps?.longitude || '0',
          address: addressText.value || '0',
        })
        return false
      }
    } catch (e: any) {
      sessionStorage.setItem(LOCATION_PERMISSION_STATUS, "denied")
      GoogleAnalytics.locationEvent({
        [EventParameter.ACTION]: 'location_permission_denied',
        reason: e?.message || 'user_denied_error',
        location_available:"no",
        prompt_type:"unknown", 
        needs_location_fetch:"yes",
        lat : location.gps?.latitude || '0',
        long : location.gps?.longitude || '0',
        address: addressText.value || '0',

      })
      return false
    }
  }

  // -------------------- Fetch Location --------------------
  const fetchLocation = async () => {
    isFetchingLocation.value = true
    loadingMessage.value = "Fetching your current location..."

    try {
      const hasPermission = await ensureLocationPermission()
      console.log("[LOCATION] Has permission:", hasPermission)
      if (!hasPermission) return

      const coords = await getLocationOnceSafe()

      if (coords) {
        console.log("[LOCATION] Coordinates:", coords)
        await geolocation.setLocationByCoordinates(coords.latitude, coords.longitude)
      }
    } catch (err: any) {
      console.log("[LOCATION] Error fetching location:", err)
    } finally {
      isFetchingLocation.value = false
      loadingMessage.value = ""
    }
  }

  // -------------------- Should Fetch --------------------
  const shouldFetchLocation = () => {
    const hasLocation = Boolean(geolocation.isValidLocation)
    const needsLocationFetch = !hasLocation

    // GA: track if user needs location fetch
    GoogleAnalytics.locationEvent({
      [EventParameter.ACTION]: 'checkLocationPermission',
      location_available: hasLocation ? "yes" : "no",       
      needs_location_fetch: needsLocationFetch ? "yes" : "no" ,
      reason : 'unknown',
      prompt_type:"unknown",
      lat : location.gps?.latitude || '0',
      long : location.gps?.longitude || '0',
      address: addressText.value || '0',
      
    })

    return needsLocationFetch
  }

  // -------------------- Compare Location --------------------
  const handleLocationCompare = async () => {
    const partnerCode = runtimeConfig.public.partnerCode?.toLowerCase()
    if (excludePartners.includes(partnerCode)) return
    if (location?.gps?.latitude == null || location?.gps?.longitude == null) return

    const hasPermission = await ensureLocationPermission()
    if (!hasPermission) return

    checkIfLocationsAreSame()
  }

  // -------------------- Lifecycle --------------------
  onMounted(async () => {
    console.log("[LOCATION] Auto fetch location mounted")
    const needsFetch = shouldFetchLocation()

    if (needsFetch) {
      await fetchLocation()
    } else {
      console.log("[LOCATION] Handling location compare")
      await handleLocationCompare()
    }
  })

  return {
    isFetchingLocation,
    loadingMessage,
    fetchLocation,
    checkIfLocationsAreSame
  }
}